import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Icon, Badge, Rating, Button, Skeleton, EmptyState } from '../../components/ui';
import { fetchAllHotels } from '../../services/hotelService';

const FALLBACK_IMG = '/images/design/hero-ram-mandir.webp';

const CATEGORIES = [
  { id: 'dharamshala', label: 'Dharamshala' },
  { id: 'budget', label: 'Budget' },
  { id: 'standard', label: 'Standard' },
  { id: 'premium', label: 'Premium' },
  { id: 'boutique', label: 'Boutique' },
  { id: 'heritage', label: 'Heritage' },
  { id: 'luxury', label: 'Luxury' },
];

const AMENITY_FILTERS = [
  { id: 'wifi', label: 'Free WiFi', match: /wi-?fi/i, icon: 'wifi' },
  { id: 'parking', label: 'Parking', match: /parking/i, icon: 'car' },
  { id: 'restaurant', label: 'Restaurant', match: /restaurant|dining|food/i, icon: 'utensils' },
  { id: 'ac', label: 'Air conditioning', match: /\bac\b|air.?condition/i, icon: 'sparkles' },
];

const AMENITY_ICON = [
  [/wi-?fi/i, 'wifi'], [/parking/i, 'car'], [/restaurant|dining|food|breakfast/i, 'utensils'],
  [/pool|swim/i, 'waves'], [/spa|ayurved|yoga/i, 'sparkles'], [/temple|darshan|tour/i, 'temple'],
  [/room service|service|concierge|butler/i, 'headset'], [/transfer|airport|shuttle/i, 'car'],
];
const amenityIcon = (a = '') => (AMENITY_ICON.find(([re]) => re.test(a)) || [null, 'circle-check'])[1];

const firstPrice = (priceRange) => {
  const m = String(priceRange || '').replace(/[, ]/g, '').match(/(\d+)/);
  return m ? Number(m[1]) : null;
};
const distanceKm = (h) => {
  const d = h.distanceFromLandmarks || {};
  const v = d['Ram Janmabhoomi'] ?? d['Ram Mandir'] ?? d['Ram Janmabhoomi Mandir'];
  return v == null ? null : Number(v);
};

export default function Hotels() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const q = params.get('q') || '';
  const sort = params.get('sort') || 'rating';
  const stars = params.get('stars') || 'any';
  const cats = (params.get('category') || '').split(',').filter(Boolean);
  const amens = (params.get('amenities') || '').split(',').filter(Boolean);
  const checkIn = params.get('checkIn');
  const checkOut = params.get('checkOut');
  const guests = params.get('guests');

  const patch = (mutate) => { const next = new URLSearchParams(params); mutate(next); setParams(next, { replace: true }); };
  const setOne = (k, v, empty) => patch((n) => { if (!v || v === empty) n.delete(k); else n.set(k, v); });
  const toggleList = (k, list, id) => {
    const set = new Set(list);
    if (set.has(id)) set.delete(id); else set.add(id);
    patch((n) => { const v = Array.from(set).join(','); if (v) n.set(k, v); else n.delete(k); });
  };
  const clearFilters = () => setParams(new URLSearchParams(), { replace: true });

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(false);
    fetchAllHotels({ limit: 100 })
      .then((data) => { if (!alive) return; setHotels(Array.isArray(data) ? data : data?.data || []); })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [reload]);

  const list = useMemo(() => hotels.map((h) => ({
    id: h.id || h._id,
    name: h.name,
    category: h.category || h.type || '',
    starRating: Number(h.starRating) || 0,
    rating: Number(h.rating) || null,
    reviews: h.reviewCount || null,
    price: firstPrice(h.priceRange) ?? Number(h.pricePerNight) ?? null,
    priceRange: h.priceRange,
    image: h.images?.[0] || FALLBACK_IMG,
    amenities: Array.isArray(h.amenities) ? h.amenities : [],
    km: distanceKm(h),
    location: h.location,
    description: h.description || '',
  })), [hotels]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = list.filter((h) => {
      if (cats.length && !cats.includes(String(h.category).toLowerCase())) return false;
      if (stars !== 'any' && h.starRating < Number(stars)) return false;
      if (amens.length) {
        const ok = amens.every((id) => {
          const f = AMENITY_FILTERS.find((x) => x.id === id);
          return f && h.amenities.some((a) => f.match.test(a));
        });
        if (!ok) return false;
      }
      if (needle && !`${h.name} ${h.location || ''} ${h.description}`.toLowerCase().includes(needle)) return false;
      return true;
    });
    out = [...out];
    if (sort === 'price-asc') out.sort((a, b) => (a.price ?? 1e9) - (b.price ?? 1e9));
    else if (sort === 'price-desc') out.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
    else out.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
    return out;
  }, [list, q, cats, stars, amens, sort]);

  const catCount = (id) => list.filter((h) => String(h.category).toLowerCase() === id).length;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Stay & Accommodation in Ayodhya',
    description: 'Verified hotels, dharamshalas and homestays near Shri Ram Janmabhoomi Mandir, Ayodhya.',
    url: 'https://ayodhyadham.info/hotels',
    about: { '@type': 'City', name: 'Ayodhya', address: { '@type': 'PostalAddress', addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', addressCountry: 'IN' } },
    ...(results.length > 0 ? {
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: results.length,
        itemListElement: results.slice(0, 10).map((h, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'LodgingBusiness',
            name: h.name,
            description: h.description || `${h.category} near Ram Mandir, Ayodhya`,
            url: `https://ayodhyadham.info/hotels/${h.id}`,
            priceRange: h.priceRange || (h.price ? `₹${h.price}` : undefined),
            ...(h.rating ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: h.rating, reviewCount: h.reviews || 1 } } : {}),
            address: { '@type': 'PostalAddress', addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', addressCountry: 'IN' },
          },
        })),
      },
    } : {}),
  };

  const searchChips = [
    checkIn && { icon: 'calendar', text: `${t('Check-in')}: ${checkIn}` },
    checkOut && { icon: 'calendar', text: `${t('Check-out')}: ${checkOut}` },
    guests && { icon: 'users', text: `${guests} ${Number(guests) === 1 ? t('Guest') : t('Guests')}` },
  ].filter(Boolean);

  return (
    <>
      <SEO
        title="Hotels & Stays in Ayodhya"
        description="Find verified stays near Ram Mandir — from ₹999 dharamshalas to heritage hotels and boutique retreats."
        canonicalUrl="/hotels"
        keywords="hotels in Ayodhya, hotels near Ram Mandir, Ayodhya accommodation, dharamshala Ayodhya"
        structuredData={schema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Stay & Accommodation') }]} title={t('Stay & Accommodation')} subtitle={t('Verified hotels, dharamshalas and homestays near Ram Mandir')} />

      <div className="container listing-wrap">
        <div className="listing__toolbar">
          {searchChips.length > 0 && (
            <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
              {searchChips.map((c) => (
                <Badge key={c.text} variant="soft"><Icon name={c.icon} size={13} />{c.text}</Badge>
              ))}
            </div>
          )}
          <div className="listing__tools">
            <label className="search">
              <Icon name="search" size={16} />
              <span className="sr-only">{t('Search stays')}</span>
              <input type="search" placeholder={t('Search by name or area…')} value={q} onChange={(e) => setOne('q', e.target.value, '')} />
            </label>
            <label className="sort">
              <Icon name="arrow-up-down" size={15} />
              <span className="sr-only">{t('Sort by')}</span>
              <select value={sort} onChange={(e) => setOne('sort', e.target.value, 'rating')}>
                <option value="rating">{t('Top rated')}</option>
                <option value="price-asc">{t('Price: low to high')}</option>
                <option value="price-desc">{t('Price: high to low')}</option>
              </select>
            </label>
            <button type="button" className="btn btn--ghost btn--sm filters__toggle" onClick={() => setFiltersOpen((o) => !o)} aria-expanded={filtersOpen}>
              <Icon name="sliders-horizontal" size={15} />{t('Filters')}
            </button>
          </div>
        </div>

        <div className="listing">
          <aside className={`filters${filtersOpen ? ' is-open' : ''}`} aria-label={t('Filters')}>
            <div className="card filters__card">
              <div className="filters__head">
                <div className="filters__title">{t('Filters')}</div>
                <button type="button" className="filters__clear" onClick={clearFilters}>{t('Clear all')}</button>
              </div>

              <div className="filters__group">
                <div className="filters__group-title">{t('Stay type')}</div>
                {CATEGORIES.map((c) => (
                  <label key={c.id} className="filters__row">
                    <input type="checkbox" checked={cats.includes(c.id)} onChange={() => toggleList('category', cats, c.id)} />
                    {t(c.label)}<span className="count">{catCount(c.id)}</span>
                  </label>
                ))}
              </div>

              <div className="filters__group">
                <div className="filters__group-title">{t('Star rating')}</div>
                {[['any', t('Any')], ['3', t('3 star & up')], ['4', t('4 star & up')], ['5', t('5 star')]].map(([id, label]) => (
                  <label key={id} className="filters__row">
                    <input type="radio" name="stars" checked={stars === id} onChange={() => setOne('stars', id, 'any')} />{label}
                  </label>
                ))}
              </div>

              <div className="filters__group">
                <div className="filters__group-title">{t('Amenities')}</div>
                {AMENITY_FILTERS.map((a) => (
                  <label key={a.id} className="filters__row">
                    <input type="checkbox" checked={amens.includes(a.id)} onChange={() => toggleList('amenities', amens, a.id)} />
                    {t(a.label)}
                  </label>
                ))}
              </div>
            </div>
          </aside>

          <div>
            <div className="results-meta">
              <span>{loading ? t('Loading stays…') : t('{{count}} stays found', { count: results.length })}</span>
              {(cats.length > 0 || amens.length > 0 || stars !== 'any' || q) && (
                <button type="button" className="link-arrow" onClick={clearFilters}>{t('Clear filters')}<Icon name="x" size={14} /></button>
              )}
            </div>

            {loading ? (
              <div className="card-grid card-grid--wide">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={360} radius="var(--r-lg)" />)}</div>
            ) : error ? (
              <EmptyState icon="circle-alert" title={t('Could not load stays')} body={t('The server is not reachable right now. Please try again.')} action={<Button variant="outline" onClick={() => setReload((k) => k + 1)}>{t('Retry')}</Button>} />
            ) : results.length === 0 ? (
              <EmptyState icon="bed-double" title={t('No stays match your filters')} body={t('Try widening your search or clearing the filters.')} action={<Button variant="outline" onClick={clearFilters}>{t('Clear filters')}</Button>} />
            ) : (
              <div className="card-grid card-grid--wide">
                {results.map((h) => (
                  <article key={h.id} className="card card--hover hotel-card">
                    <div className="card__media">
                      <img src={h.image} alt={h.name} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} />
                      {h.category && <Badge variant="soft" className="hotel-card__cat">{t(h.category.charAt(0).toUpperCase() + h.category.slice(1))}</Badge>}
                    </div>
                    <div className="card__body">
                      <div className="hotel-card__head">
                        <h3 className="h-card">{h.name}</h3>
                        {h.rating != null && <Rating value={h.rating} count={h.reviews} />}
                      </div>
                      {(h.km != null || h.location) && (
                        <div className="hotel-card__loc">
                          <Icon name="map-pin" size={14} />
                          <span>{h.km != null ? t('{{km}} km from Ram Mandir', { km: h.km }) : h.location}</span>
                        </div>
                      )}
                      {h.amenities.length > 0 && (
                        <div className="amenities">
                          {h.amenities.slice(0, 3).map((a) => (
                            <span key={a} className="badge badge--outline"><Icon name={amenityIcon(a)} size={13} />{a}</span>
                          ))}
                          {h.amenities.length > 3 && <span className="badge badge--muted">+{h.amenities.length - 3}</span>}
                        </div>
                      )}
                      <div className="hotel-card__foot">
                        <div className="price">
                          {h.price ? <>₹{h.price.toLocaleString('en-IN')} <small>{t('/ night')}</small></> : <small>{t('Price on request')}</small>}
                        </div>
                        <Button size="sm" to={`/hotels/${h.id}`} icon={<Icon name="arrow-right" size={14} />}>{t('View details')}</Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
